/**
 * BottomNav — mobile-only bottom navigation bar for parent users.
 *
 * 5 slots — More (menu) sharing the Sidebar drawer's edge, flagship (Chat) in
 * the CENTER. Default (left-anchored drawer):
 *
 *   More · Home · Chat · Calendar · Finances
 *
 *   - "More" sits on the drawer's edge because it opens the Sidebar drawer:
 *     trigger and drawer share a side so a tap and the panel meet at the same
 *     hand. With the right-side-sidebar setting (side='right') the drawer slides
 *     in from the right, so More mirrors to the LAST slot:
 *
 *       Home · Chat · Calendar · Finances · More
 *
 *     Opens via onMore (setSidebarOpen(true) in MainLayout). The drawer holds
 *     the full grouped nav + Settings / Notifications / Help / profile /
 *     sign-out.
 *   - Chat (the 24/7 companion) takes the center slot for prominence.
 *   - Tasks is reached from the Calendar (tasks render alongside events) and the
 *     sidebar, so its slot went to Finances (receipts/expenses — a core TRF
 *     surface). Grants live as a tab of Finances (/bills?tab=grants; the old
 *     /grants URL just redirects there) — the Finances slot covers them, no
 *     bar slot of their own.
 *
 * Conventions:
 *   - A label may wrap to a second line (es "Apoyos económicos", fr, pt at 390 px;
 *     #2755). Icons hang from one line across every slot (justify-start, not
 *     centred), so a two-line label never lifts its icon above the others;
 *     the label centres under its icon and is never truncated.
 *   - Material 3 Bottom Navigation Bar + iOS HIG Tab Bar (icon + label,
 *     active item indigo, h-16)
 *   - safe-area-inset-bottom honored — bar clears the gesture nav
 *   - No position:fixed — flex-shrink-0 sibling of <main> in MainLayout's
 *     fixed app shell (B-039)
 *   - sm:hidden — desktop keeps the sidebar primary nav
 *   - Renders only for role='parent' (gate is in MainLayout.tsx)
 */
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Home, MessageSquare, Calendar, FileText, MoreHorizontal } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { NAV_KEY, navLabel, type NavKey } from '@/i18n/keys/nav';

/** A tab label: centred under its icon, free to wrap to a second line, never clipped. */
const TAB_LABEL = 'text-center leading-tight [overflow-wrap:anywhere]';

interface PrimaryNavItem {
  to: string;
  /** Resolved through `t()` at render (#2109 L6) — the tab is a nav label. */
  labelKey: NavKey;
  icon: LucideIcon;
}

// Order puts the flagship companion (Chat) in the center slot:
//   More · Home · Chat · Calendar · Finances
const PRIMARY: PrimaryNavItem[] = [
  // "Home", deliberately, while the sidebar item for the same route is
  // "Today" (design grammar §4.4, ratified DECISIONS 2026-08-26). A tab-bar
  // slot is the shell's home AFFORDANCE — the way back to the start of the
  // app — which is a different part of speech from the page's name. §4.4
  // states this exception in the same paragraph that renames the sidebar
  // item, and `eyebrow-parity.test.ts` pins it so a future sweep does not
  // "finish" the rename here.
  { to: '/dashboard', labelKey: NAV_KEY.tabHome,  icon: Home },
  { to: '/chat',      labelKey: NAV_KEY.tabChat,  icon: MessageSquare },
  // The same key as the sidebar row: one destination, one label (§4.4).
  { to: '/calendar',  labelKey: NAV_KEY.calendar, icon: Calendar },
  // Renamed with the sidebar row and the page eyebrow (2026-09-21).
  { to: '/bills',     labelKey: NAV_KEY.grants,   icon: FileText },
];

export function BottomNav({ onMore, side = 'left' }: { onMore?: () => void; side?: 'left' | 'right' }) {
  const { t } = useTranslation('shell');
  // Menu trigger shares the sidebar drawer's edge so tap and panel meet at the
  // same hand: left-anchored drawer → first slot, right-anchored → last slot.
  const moreSlot = (
    <li key="more" className="flex-1">
      <button
        type="button"
        onClick={() => onMore?.()}
        aria-label={t('bottomNav.openMenu')}
        // The onboarding guide chip points here on a phone, where the nav
        // link it guides to sits in the closed drawer (#2385).
        data-guide="more-btn"
        className={cn(
          'flex h-full w-full min-w-0 flex-col items-center justify-start gap-0.5 px-1 pt-2',
          'text-caption text-stevens-ink-2 transition-colors hover:text-stevens-ink active:bg-stevens-bg-2',
        )}
      >
        <MoreHorizontal className="h-5 w-5" aria-hidden="true" />
        <span className={TAB_LABEL}>{navLabel(NAV_KEY.tabMore)}</span>
      </button>
    </li>
  );

  const primarySlots = PRIMARY.map((item) => {
    const Icon = item.icon;
    return (
      <li key={item.to} className="flex-1">
        <NavLink
          to={item.to}
          className={({ isActive }) =>
            cn(
              'flex h-full w-full min-w-0 flex-col items-center justify-start gap-0.5 px-1 pt-2',
              'text-caption transition-colors active:bg-stevens-bg-2',
              isActive
                ? 'text-stevens-indigo font-medium'
                : 'text-stevens-ink-2 hover:text-stevens-ink',
            )
          }
          end={item.to === '/dashboard'}
        >
          <Icon className="h-5 w-5" aria-hidden="true" />
          <span className={TAB_LABEL}>{navLabel(item.labelKey)}</span>
        </NavLink>
      </li>
    );
  });

  return (
    <nav
      className="sm:hidden flex-shrink-0 border-t border-stevens-rule-2 bg-stevens-bg pb-[env(safe-area-inset-bottom)]"
      aria-label={t('bottomNav.primaryNavigation')}
    >
      <ul className="flex h-16 items-stretch">
        {side === 'right' ? [...primarySlots, moreSlot] : [moreSlot, ...primarySlots]}
      </ul>
    </nav>
  );
}
